// src/components/enduser/dashboard/ImportSelectionPage.tsx - עם תיקון אנימציות

import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useEnduserAuth } from "../../../contexts/EnduserAuthContext";
import { useMotionSafe } from "../../../hooks/useMotionSafe";
import M1CarIcon from "../../../assets/icons/m1-petrol-diesel-car.svg";

const FREE_TRIAL_OFFER_SEEN_KEY = "cartax_free_trial_offer_seen";

const ImportSelectionPage: React.FC = () => {
  const {
    user,
    subscriptionStatus,
    canAccessCalculators,
    getSubscriptionMessage,
    verifySession,
  } = useEnduserAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // ניסיון חינם: פופאפ הצעה למי שזכאי וטרם ראה
  const [showFreeTrialPopup, setShowFreeTrialPopup] = useState(false);

  // Reference to track if component is mounted
  const isMounted = useRef(true);

  // Refresh subscription status on mount and every 5 minutes
  useEffect(() => {
    // Initial refresh when page loads
    verifySession();

    // Set up interval to refresh every 5 minutes (300000ms)
    const intervalId = setInterval(() => {
      if (isMounted.current) {
        verifySession();
      }
    }, 5 * 60 * 1000); // 5 minutes

    // Cleanup on unmount
    return () => {
      isMounted.current = false;
      clearInterval(intervalId);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const { shouldAnimate, getAnimationClass, getHoverClass } = useMotionSafe();

  // ===== ניסיון חינם בעזרת קוד אימות (מאי 2026) =====
  // ניסיון פעיל → מציג badge יתרה. זכאי (ללא מנוי, טרם הפעיל, 0 ניצול) → פופאפ + paywall לניסיון.
  const isFreeTrialActive = subscriptionStatus?.type === "free_trial";
  const freeTrialRemaining = subscriptionStatus?.free_trial_remaining ?? 0;
  const freeTrialMax = subscriptionStatus?.free_trial_max ?? 2;

  const isEligibleForFreeTrial =
    !user?.free_access &&
    !canAccessCalculators() &&
    subscriptionStatus?.type === "no_subscription" &&
    !subscriptionStatus?.free_trial_activated &&
    (subscriptionStatus?.free_trial_used ?? 0) === 0;

  // המפתח ב-localStorage לפי משתמש - כדי שדגל ה"נראה" לא ידלוף בין חשבונות במכשיר משותף.
  const offerSeenKey = user?.id
    ? `${FREE_TRIAL_OFFER_SEEN_KEY}_${user.id}`
    : FREE_TRIAL_OFFER_SEEN_KEY;

  const hasSeenOffer = (): boolean => {
    try {
      return localStorage.getItem(offerSeenKey) === "1";
    } catch {
      return false;
    }
  };

  const markOfferSeen = () => {
    try {
      localStorage.setItem(offerSeenKey, "1");
    } catch {
      /* localStorage חסום - מתעלמים, הפופאפ פשוט עלול לחזור */
    }
  };

  // הצגת הפופאפ פעם אחת למשתמש זכאי שטרם ראה
  useEffect(() => {
    if (isEligibleForFreeTrial && !hasSeenOffer()) {
      setShowFreeTrialPopup(true);
    }
  }, [isEligibleForFreeTrial]); // eslint-disable-line react-hooks/exhaustive-deps

  const dismissFreeTrialPopup = () => {
    markOfferSeen();
    setShowFreeTrialPopup(false);
  };

  const goToFreeTrial = () => {
    markOfferSeen();
    setShowFreeTrialPopup(false);
    navigate("/enduser/free-trial");
  };

  // הודעת הצלחה אחרי הפעלת ניסיון (מגיעה מ-FreeTrialFlow דרך navigate state)
  const justActivated = Boolean(
    (location.state as { freeTrialActivated?: boolean } | null)
      ?.freeTrialActivated
  );

  return (
    <div
      className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8"
      dir="rtl"
    >
      {/* ===== פופאפ הצעת ניסיון חינם (פעם אחת למשתמש זכאי) ===== */}
      {showFreeTrialPopup && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 px-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="free-trial-popup-title"
          onClick={dismissFreeTrialPopup}
        >
          <div
            className="relative max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={dismissFreeTrialPopup}
              className="absolute top-3 left-3 text-gray-400 hover:text-gray-600"
              aria-label="סגירת חלון ההצעה"
            >
              <svg
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>

            <div className="text-6xl mb-4" aria-hidden="true">
              🎟️
            </div>
            <h2
              id="free-trial-popup-title"
              className="text-2xl font-bold text-gray-900 mb-2"
            >
              קבלו {freeTrialMax} חישובים בחינם!
            </h2>
            <p className="text-gray-600 mb-6">
              לפני שמתחייבים למנוי - נסו את CarTax בחינם. הפעלת הניסיון אורכת דקה,
              בעזרת קוד אימות קצר שיישלח אליכם.
            </p>

            <button
              type="button"
              onClick={goToFreeTrial}
              className={`w-full inline-flex items-center justify-center px-5 py-3 text-base font-medium rounded-md text-white bg-green-600 ${getHoverClass(
                "hover:bg-green-700 transition-colors"
              )}`}
            >
              אני רוצה להתחיל ניסיון חינם
            </button>
            <button
              type="button"
              onClick={dismissFreeTrialPopup}
              className="mt-3 w-full text-sm text-gray-500 hover:text-gray-700 underline"
            >
              אולי מאוחר יותר
            </button>
          </div>
        </div>
      )}

      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          {/* Mobile Layout */}
          <div className="md:hidden flex flex-col items-center mb-4">
            <img
              src={M1CarIcon}
              alt=""
              className="w-24 h-24 mb-1"
              aria-hidden="true"
            />
            <h1 className="text-3xl font-bold text-gray-900">ברוכים הבאים</h1>
            <h1 className="text-3xl font-bold text-gray-900">ל-CarTax</h1>
          </div>

          {/* Desktop Layout */}
          <h1 className="hidden md:flex items-center justify-center text-4xl font-bold text-gray-900 mb-4">
            <img
              src={M1CarIcon}
              alt=""
              className="w-28 h-28 ml-5"
              aria-hidden="true"
            />
            ברוכים הבאים ל-CarTax
          </h1>
          <p className="text-xl text-gray-600">
            שלום {user?.name || "משתמש"}, בחר את סוג הייבוא המתאים עבורך
          </p>

          {/* Subscription Status */}
          <div className="mt-6 inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-blue-50 text-blue-800">
            <span className="text-lg ml-2">
              {user?.free_access ? "🎉" : "📋"}
            </span>
            {getSubscriptionMessage()}
          </div>

          {/* ניסיון חינם: badge יתרת ניסיונות (כשהניסיון פעיל) */}
          {isFreeTrialActive && (
            <div className="mt-4">
              <span
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold bg-green-100 text-green-800 border border-green-300"
                aria-label={`ניסיון חינם פעיל, נותרו ${freeTrialRemaining} חישובים מתוך ${freeTrialMax}`}
              >
                <span className="text-lg" aria-hidden="true">
                  🎟️
                </span>
                ניסיון חינם פעיל · נותרו {freeTrialRemaining}/{freeTrialMax} חישובים
              </span>
            </div>
          )}

          {/* הודעת הצלחה חד-פעמית אחרי הפעלת ניסיון */}
          {justActivated && (
            <div className="mt-4 inline-flex items-center px-4 py-2 rounded-lg text-sm font-medium bg-green-50 text-green-700 border border-green-200">
              ✅ הניסיון החינם הופעל! יש לך עכשיו {freeTrialMax} חישובים חינם.
            </div>
          )}
        </div>

        {/* Import Type Selection - עם אייקונים חדשים וצבעים זהים */}
        <div className="grid md:grid-cols-2 gap-8 mb-12">
          {/* Personal Import */}
          <Link
            to="/enduser/calculators/personal-import"
            className={`group bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden ${getHoverClass(
              "hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2"
            )}`}
          >
            <div className="p-12 text-center">
              <div className="mb-8 text-center">
                <h2
                  className={`text-3xl font-bold text-black mb-2 ${getHoverClass(
                    "group-hover:text-blue-600 transition-colors"
                  )}`}
                >
                  יבוא אישי
                </h2>
                <p className="text-xl text-gray-600">שימוש פרטי</p>
              </div>

              <div
                className={`flex items-center justify-center text-blue-600 font-medium text-lg ${getHoverClass(
                  "group-hover:text-blue-700 transition-colors"
                )}`}
              >
                <span>לחץ כאן</span>
                <svg
                  className={`w-6 h-6 mr-3 ${getAnimationClass(
                    "transform group-hover:translate-x-1 transition-transform"
                  )}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 7l5 5m0 0l-5 5m5-5H6"
                  />
                </svg>
              </div>
            </div>

            {/* Hover Effect Bar */}
            {shouldAnimate && (
              <div className="h-2 bg-gradient-to-r from-blue-500 to-blue-600 transform scale-x-0 group-hover:scale-x-100 transition-transform origin-right"></div>
            )}
          </Link>

          {/* Commercial Import - צבעים זהים */}
          <Link
            to="/enduser/calculators/commercial-import"
            className={`group bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden ${getHoverClass(
              "hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2"
            )}`}
          >
            <div className="p-12 text-center">
              <div className="mb-8 text-center">
                <h2
                  className={`text-3xl font-bold text-black mb-2 ${getHoverClass(
                    "group-hover:text-blue-600 transition-colors"
                  )}`}
                >
                  יבוא מסחרי
                </h2>
                <p className="text-xl text-gray-600">לצורך מכירה</p>
              </div>

              {/* Coming Soon Badge */}
              {/* <div className="mb-4">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                  🚧 בפיתוח
                </span>
              </div> */}

              <div
                className={`flex items-center justify-center text-blue-600 font-medium text-lg ${getHoverClass(
                  "group-hover:text-blue-700 transition-colors"
                )}`}
              >
                <span>לחץ כאן</span>
                <svg
                  className={`w-6 h-6 mr-3 ${getAnimationClass(
                    "transform group-hover:translate-x-1 transition-transform"
                  )}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 7l5 5m0 0l-5 5m5-5H6"
                  />
                </svg>
              </div>
            </div>

            {/* Hover Effect Bar - צבע זהה */}
            {shouldAnimate && (
              <div className="h-2 bg-gradient-to-r from-blue-500 to-blue-600 transform scale-x-0 group-hover:scale-x-100 transition-transform origin-right"></div>
            )}
          </Link>
        </div>

        {/* Quick Stats - עם טקסט מעודכן */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          <div
            className={`bg-white rounded-lg shadow p-6 text-center ${getHoverClass(
              "hover:shadow-md transition-shadow"
            )}`}
          >
            <div className="text-3xl text-blue-600 font-bold">✓</div>
            <div className="text-gray-600 mt-2">בדיקת עלויות אישית</div>
          </div>
          <div
            className={`bg-white rounded-lg shadow p-6 text-center ${getHoverClass(
              "hover:shadow-md transition-shadow"
            )}`}
          >
            <div className="text-3xl text-blue-600 font-bold">🏢</div>
            <div className="text-gray-600 mt-2">כניסה למערכת מסחרית</div>
          </div>
          <div
            className={`bg-white rounded-lg shadow p-6 text-center ${getHoverClass(
              "hover:shadow-md transition-shadow"
            )}`}
          >
            <div className="text-3xl text-blue-600 font-bold">24/7</div>
            <div className="text-gray-600 mt-2">זמינות המערכת</div>
          </div>
        </div>

        {/* Access Status Warning */}
        {!canAccessCalculators() &&
          (isEligibleForFreeTrial ? (
            /* זכאי לניסיון חינם → CTA לניסיון במקום ישירות למנוי */
            <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
              <div className="text-5xl mb-3" aria-hidden="true">
                🎟️
              </div>
              <h3 className="text-lg font-medium text-green-800 mb-2">
                נסו את CarTax בחינם - {freeTrialMax} חישובים
              </h3>
              <p className="text-green-700 mb-4">
                הפעילו ניסיון חינם בעזרת קוד אימות קצר ותוכלו לבצע {freeTrialMax} חישובים סופיים
                ללא תשלום.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <button
                  type="button"
                  onClick={goToFreeTrial}
                  className={`inline-flex items-center px-5 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 ${getHoverClass(
                    "hover:bg-green-700 transition-colors"
                  )}`}
                >
                  הפעלת ניסיון חינם
                </button>
                <Link
                  to="/enduser/subscription"
                  className="text-sm text-gray-600 hover:text-gray-800 underline"
                >
                  או רכישת מנוי מלא
                </Link>
              </div>
            </div>
          ) : (
            /* מיצה ניסיון / כל מצב חוסר-גישה אחר → paywall רגיל */
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
              <div className="text-yellow-600 mb-4">
                <svg
                  className="mx-auto h-12 w-12"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-yellow-800 mb-2">
                נדרש מנוי לגישה למערכת CarTax
              </h3>
              <p className="text-yellow-700 mb-4">
                {(subscriptionStatus?.free_trial_used ?? 0) >= freeTrialMax
                  ? `ניצלת את ${freeTrialMax} החישובים החינמיים. כדי להמשיך, יש להירשם כמנוי.`
                  : "כדי להשתמש במערכת CarTax, יש להירשם כמנוי"}
              </p>
              <Link
                to="/enduser/subscription"
                className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-yellow-600 ${getHoverClass(
                  "hover:bg-yellow-700 transition-colors"
                )}`}
              >
                רכישת מנוי
              </Link>
            </div>
          ))}

        {/* Help Section */}
        <div className="bg-white rounded-2xl shadow-lg p-8">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              🤝 זקוקים לעזרה?
            </h2>
            <p className="text-gray-600">
              אנחנו כאן כדי לעזור לכם בכל שלב של תהליך חישוב המיסים
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            <Link to="/enduser/explanations" className="text-center block">
              <div
                className={`bg-blue-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4 ${getHoverClass(
                  "hover:bg-blue-200 transition-colors"
                )}`}
              >
                <span className="text-2xl">📖</span>
              </div>
              <h3 className="font-bold text-gray-900 mb-2">מדריכים</h3>
              <p className="text-sm text-gray-600">
                מדריכים מפורטים לכל סוג של חישוב מס
              </p>
            </Link>

            <a
              href="https://wa.me/972544512136"
              target="_blank"
              rel="noopener noreferrer"
              className="text-center block"
            >
              <div
                className={`bg-green-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4 ${getHoverClass(
                  "hover:bg-green-200 transition-colors"
                )}`}
              >
                <svg
                  className="w-8 h-8 text-green-600"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                </svg>
              </div>
              <h3 className="font-bold text-gray-900 mb-2">תמיכה בוואצאפ</h3>
              <p className="text-sm text-gray-600">
                צוות התמיכה שלנו זמין לכל שאלה או בעיה
              </p>
            </a>

            <a href="mailto:info@cartax.org" className="text-center block">
              <div
                className={`bg-blue-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4 ${getHoverClass(
                  "hover:bg-blue-200 transition-colors"
                )}`}
              >
                <svg
                  className="w-8 h-8 text-blue-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>
              </div>
              <h3 className="font-bold text-gray-900 mb-2">תמיכה במייל</h3>
              <p className="text-sm text-gray-600">
                צוות התמיכה שלנו זמין גם במייל
              </p>
            </a>
            <Link to="/enduser/explanations" className="text-center block">
              <div
                className={`bg-blue-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4 ${getHoverClass(
                  "hover:bg-blue-200 transition-colors"
                )}`}
              >
                <span className="text-2xl">📝</span>
              </div>
              <h3 className="font-bold text-gray-900 mb-2">הסברי חישוב</h3>
              <p className="text-sm text-gray-600">
                הסברים מפורטים על אופן חישוב המיסים
              </p>
            </Link>
          </div>
        </div>
        {/* Footer Links */}
        <div className="text-center mt-8 pt-6 border-t border-gray-200">
          <div className="flex justify-center gap-6 text-sm text-gray-500">
            <Link
              to="/terms-and-privacy"
              className="hover:text-blue-600 transition-colors"
            >
              תנאי שימוש ופרטיות
            </Link>
            <span>|</span>
            <Link
              to="/accessibility-statement"
              className="hover:text-blue-600 transition-colors"
            >
              הצהרת נגישות
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImportSelectionPage;
